import React, { useRef, useState, useEffect } from 'react';
import './GameSteps.css'; // Import your CSS file for styling
import GameContentProvider from '../providers/GameContentProvider';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import Confetti from 'react-confetti';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import Toggle from '../Toggle';
import IntroGuideOverlay from './IntroGuideOverlay';
import { FaMapMarkedAlt, FaLightbulb, FaFastForward } from 'react-icons/fa';

const loadGoogleMaps = (callback) => {
    // Detect environment based on hostname
    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

    // Use the appropriate API key
    const apiKey = isLocalhost
        ? "AIzaSyCLltv9TtdzLnNWKwgpN7eG10IlKK674uQ"    // Localhost API Key
        : "AIzaSyATNBFtqIHepH9en1I-sU80qnMBywHI7TI";
    // Production API Key

    if (typeof window.google === 'undefined' || typeof window.google.maps === 'undefined') {
        const existingScript = document.getElementById('googleMaps');
        if (!existingScript) {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
            script.id = 'googleMaps';
            script.async = true;
            script.defer = true;

            script.onload = () => {
                if (window.google && window.google.maps) {
                    callback();
                } else {
                    const interval = setInterval(() => {
                        if (window.google && window.google.maps) {
                            clearInterval(interval);
                            callback();
                        }
                    }, 100);
                }
            };

            document.body.appendChild(script);
        } else {
            existingScript.onload = () => {
                if (window.google && window.google.maps) {
                    callback();
                }
            };
        }
    } else if (callback) {
        callback();
    }
};



const MapOverlay = ({ closeOverlay, recommendations, city }) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState("");
    const [place, setPlace] = useState(null); // To store the searched place
    const [coordinates, setCoordinates] = useState(null);
    const [error, setError] = useState(null); // To store any errors
    const mapRef = useRef(null); // Ref to store the Google Map object
    const mapInstance = useRef(null);
    const searchBoxRef = useRef(null); // Ref for the SearchBox input
    const [showMarkersSelection, setShowMarkersSelection] = useState(false);
    const [toggleStates, setToggleStates] = useState([]);
    const markersRef = useRef([]);
    const searchMarkersRef = useRef([]);
    const watchIdRef = useRef(null);
    const [userLocation, setUserLocation] = useState(null);
    const [showLocationOverlay, setShowLocationOverlay] = useState(false);




    const rows = [
        { id: 1, image: "/assets/marker_grab_and_go.png", text: t('grab_go') },
        { id: 2, image: "/assets/marker_restaurants2.png", text: t('restaurants') },
        { id: 3, image: "/assets/marker_games.png", text: t('activities') },
        { id: 4, image: "/assets/marker_information.png", text: t('information') },
        { id: 5, image: "/assets/marker_bar.png", text: t('bar') },
    ];

    const handleToggleChange = (index, newValue) => {
        const updatedStates = [...toggleStates];
        updatedStates[index] = newValue;
        setToggleStates(updatedStates);
        localStorage.setItem('toggleStates', JSON.stringify(updatedStates));

        updateMarkers();
    };

    const sofiaBounds = { north: 42.8400, south: 42.6000, east: 23.4200, west: 23.1000 };
    const sofiaLat = 42.6977, sofiaLong = 23.3219;

    const varnaBounds = { north: 43.2500, south: 43.1500, east: 28.0500, west: 27.8500 };
    const varnaLat = 43.2044, varnaLong = 27.9174;

    const plovdivBounds = { north: 42.1800, south: 42.1000, east: 24.8000, west: 24.6000 };
    const plovdivLat = 42.1354, plovdivLong = 24.7453;

    let bounds, centerLat, centerLong;
    if (city === "sofia") {
        bounds = sofiaBounds;
        centerLat = sofiaLat;
        centerLong = sofiaLong;
    } else if (city === "varna") {
        bounds = varnaBounds;
        centerLat = varnaLat;
        centerLong = varnaLong;
    } else if (city === "plovdiv") {
        bounds = plovdivBounds;
        centerLat = plovdivLat;
        centerLong = plovdivLong;
    }

    useEffect(() => {
        loadGoogleMaps(() => {
            initializeMap(); // Ensure the map is initialized

            if (mapInstance.current) {
                console.log("Google Maps loaded, starting location tracking...");
                startTrackingLocation();
            } else {
                console.error("Map instance is not ready yet.");
            }
        });

        // Cleanup function: Stop watching when component unmounts
        return () => {
            if (watchIdRef.current !== null) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
            }
        };
    }, []);


    const initializeMap = () => {
        if (typeof window.google !== 'undefined') {
            if (!mapInstance.current) {
                mapInstance.current = new window.google.maps.Map(mapRef.current, {
                    center: { lat: centerLat, lng: centerLong },
                    zoom: 15,
                    mapTypeControl: false,
                });
            }

            const savedStates = JSON.parse(localStorage.getItem('toggleStates'));
            setToggleStates(savedStates && Array.isArray(savedStates) ? savedStates : [true, true, true, true]);

            console.log(city);
            // Initialize search box
            const input = document.getElementById('search-box');
            searchBoxRef.current = new window.google.maps.places.SearchBox(input);
            searchBoxRef.current.setBounds(bounds);

            console.log("Set bounds listener");

            mapInstance.current.addListener('bounds_changed', () => {
                searchBoxRef.current.setBounds(mapInstance.current.getBounds());
            });
        }
    };



    // Function to update markers
    const updateMarkers = () => {
        if (!mapInstance.current || typeof window.google === 'undefined' || !window.google.maps) return;

        // Create custom icons for different recommendation types
        const icons = {
            1: { url: '/assets/marker_grab_and_go.png', scaledSize: new window.google.maps.Size(35, 50) },
            2: { url: '/assets/marker_restaurants2.png', scaledSize: new window.google.maps.Size(35, 50) },
            3: { url: '/assets/marker_games.png', scaledSize: new window.google.maps.Size(35, 50) },
            4: { url: '/assets/marker_information.png', scaledSize: new window.google.maps.Size(35, 50) },
            5: { url: '/assets/marker_bar.png', scaledSize: new window.google.maps.Size(35, 50) },
        };

        // Remove existing markers
        const markers = [];
        const map = mapInstance.current;

        // Remove existing markers from map
        markersRef.current.forEach((marker) => marker.setMap(null));
        markersRef.current = []; // Clear the markers array

        // Add markers based on recommendations and toggleStates
        if (recommendations && recommendations.length > 0) {
            recommendations.forEach((rec) => {
                if (toggleStates[rec.type - 1]) {
                    const icon = icons[rec.type] || icons[1];
                    const marker = new window.google.maps.Marker({
                        map: map,
                        position: { lat: rec.latitude, lng: rec.longitude },
                        icon: icon,
                        title: rec.name,
                    });

                    const infowindow = new window.google.maps.InfoWindow({
                        content: `<b>${rec.name}</b><br>${rec.description}`,
                    });

                    marker.addListener('click', () => {
                        infowindow.open({
                            anchor: marker,
                            map: map,
                            shouldFocus: false,
                        });
                    });

                    markersRef.current.push(marker);
                }
            });
        }

        // Cleanup markers on component unmount or when recommendations/toggleStates change
        return () => {
            markers.forEach((marker) => marker.setMap(null));
        };
    };


    const clearSearchMarkers = () => {
        if (searchMarkersRef.current && searchMarkersRef.current.length > 0) {
            searchMarkersRef.current.forEach((marker) => {
                if (marker && marker.setMap) {
                    marker.setMap(null); // Remove marker from the map
                }
            });
            searchMarkersRef.current = []; // Clear the array
        }

    };



    useEffect(() => {
        if (!mapInstance.current || !window.google || typeof window.google === 'undefined') return;

        if (coordinates) {
            console.log("Setting center:", coordinates);

            // Clear existing search markers
            clearSearchMarkers();

            // Set the new center of the map
            mapInstance.current.setCenter(coordinates);

            // Add a new marker at the coordinates
            const newMarker = new window.google.maps.Marker({
                position: coordinates,
                map: mapInstance.current,
            });

            // Store the new marker in the searchMarkersRef
            searchMarkersRef.current.push(newMarker);
            console.log("New marker added:", newMarker);
        }
    }, [coordinates]);

    // Effect to update markers when toggleStates or recommendations change
    useEffect(() => {
        updateMarkers();
    }, [recommendations, toggleStates]); // Only re-run this effect when these dependencies change

    const startTrackingLocation = () => {
        if (!navigator.geolocation) {
            alert("Geolocation is not supported by this browser.");
            return;
        }

        if (!window.google || !window.google.maps) {
            console.error("Google Maps API not loaded yet.");
            return;
        }

        if (watchIdRef.current !== null) return; // Prevent multiple watchers

        watchIdRef.current = navigator.geolocation.watchPosition(
            (position) => {
                const newLocation = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                };

                setUserLocation(newLocation);

                // Ensure the map instance exists
                if (!mapInstance.current) return;

                // Initialize marker if not exists
                if (!markersRef.current.currentLocationMarker) {
                    markersRef.current.currentLocationMarker = new window.google.maps.Marker({
                        position: newLocation,
                        map: mapInstance.current,
                        icon: {
                            path: window.google.maps.SymbolPath.CIRCLE,
                            fillColor: '#4285F4',
                            fillOpacity: 1,
                            strokeColor: 'white',
                            strokeWeight: 2,
                            scale: 8,
                        },
                        title: "You are here",
                    });
                } else {
                    markersRef.current.currentLocationMarker.setPosition(newLocation);
                }

                // Initialize accuracy circle if not exists
                if (!markersRef.current.accuracyCircle) {
                    markersRef.current.accuracyCircle = new window.google.maps.Circle({
                        strokeColor: '#4285F4',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: '#4285F4',
                        fillOpacity: 0.35,
                        map: mapInstance.current,
                        center: newLocation,
                        radius: position.coords.accuracy,
                    });
                } else {
                    markersRef.current.accuracyCircle.setCenter(newLocation);
                    markersRef.current.accuracyCircle.setRadius(position.coords.accuracy);
                }
            },
            (error) => {
                console.error("Error getting location:", error);
                setShowLocationOverlay(true);
            },
            { enableHighAccuracy: true }
        );
    };


    const centerMapOnUser = () => {
        if (userLocation && mapInstance.current) {
            mapInstance.current.setCenter(userLocation);
            mapInstance.current.setZoom(15);
        }
    };


    useEffect(() => {
        if (!window.google || !window.google.maps || !window.google.maps.places) {
            console.error("Google Maps API not loaded properly.");
            return;
        }

        const input = document.getElementById("search-box");
        if (!input) {
            console.error("Search input not found.");
            return;
        }

        searchBoxRef.current = new window.google.maps.places.SearchBox(input);

        searchBoxRef.current.addListener("places_changed", () => {
            const places = searchBoxRef.current.getPlaces();
            if (!places || places.length === 0) {
                console.error("No places found.");
                return;
            }

            setSearchQuery(places[0].name); // Set searchQuery to the selected place name
        });
    }, []);

    const fetchPlace = async (query) => {
        console.log(query);
        if (!window.google || !window.google.maps) {
            setError("Google Maps API not loaded properly.");
            return;
        }

        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({ address: query }, (results, status) => {
            if (status === window.google.maps.GeocoderStatus.OK) {
                console.log(results);
                const location = results[0].geometry.location;
                setPlace(results[0].formatted_address);
                setCoordinates({ lat: location.lat(), lng: location.lng() });

                setError(null);
            } else {
                setPlace(null);
                setCoordinates(null);
                setError("Place not found.");
            }
        });
    };

    const handleSearchClick = () => {
        console.log("handleSearchClick");
        if (searchQuery) {
            fetchPlace(searchQuery); // Fetch the place when the button is clicked
        }
    };

    const closeMap = () => {
        exitFullScreen();

        if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
        }

        closeOverlay('map');


    };

    const exitFullScreen = () => {
        // 1. Check if the browser is CURRENTLY in full-screen mode
        const isFullScreen = document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement;

        // 2. Only attempt to exit if we are actually in full-screen
        if (isFullScreen) {
            if (document.exitFullscreen) {
                // Catch any lingering promise rejections (standard in modern browsers)
                document.exitFullscreen().catch(err => console.warn("Fullscreen exit error:", err));
            } else if (document.mozCancelFullScreen) { // Firefox
                document.mozCancelFullScreen();
            } else if (document.webkitExitFullscreen) { // Chrome, Safari, Opera
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) { // IE/Edge
                document.msExitFullscreen();
            }
        }
    };



    useEffect(() => {
        if (!mapInstance.current || !coordinates || !window.google) return;

        mapInstance.current.setCenter(coordinates);

        new window.google.maps.Marker({
            position: coordinates,
            map: mapInstance.current,
        });
    }, [coordinates]);



    return (
        <div style={{ background: 'white', height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>

            {/* Search Bar & Controls */}
            <div style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: '10px',
                background: 'white',
                boxSizing: 'border-box',
                gap: '8px' /* Adds consistent spacing between all items */
            }}>
                <input
                    id="search-box"
                    type="text"
                    placeholder="Search for streets or places..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                        padding: '8px',
                        flex: 1, /* Takes up all remaining space instead of a fixed 60% */
                        minWidth: '0' /* Prevents flexbox from blowing out the screen width */
                    }}
                />
                <button
                    onClick={handleSearchClick}
                    style={{ padding: '8px', flexShrink: 0 }} /* flexShrink: 0 prevents squishing */
                >
                    {t('search')}
                </button>

                <div className="iconMylocation" onClick={centerMapOnUser} style={{ flexShrink: 0, cursor: 'pointer' }} />
                <div className="iconMarkers" onClick={() => setShowMarkersSelection(true)} style={{ flexShrink: 0, cursor: 'pointer' }} />
            </div>

            {/* Map Container */}
            <div style={{ flex: 1, overflow: 'hidden', width: '100%' }}>
                <div id="map" ref={mapRef} style={{ height: '100%', width: '100%' }}></div>
            </div>

            {/* Close Button */}
            <button className="button" onClick={() => closeMap()}>
                {t("close")}
            </button>

            {/* Marker Selection Overlay */}
            {showMarkersSelection && (
                <div className='overlay-steps'>
                    <div className='overlay-content'>
                        <h3>{t("chose_markers")}</h3>
                        <div>
                            {rows.map((row, index) => (
                                <div
                                    key={row.id}
                                    style={{ display: 'flex', padding: '5px', marginBottom: '5px', alignItems: 'center' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                        <img
                                            src={row.image}
                                            alt={row.text}
                                            style={{ width: '35px', height: '50px', borderRadius: '8px', objectFit: 'cover', marginRight: '10px' }}
                                        />
                                        <h4>{row.text}</h4>
                                    </div>
                                    <div style={{ position: 'absolute', left: '57%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center' }}>
                                        <Toggle
                                            checked={toggleStates[index]}
                                            onChange={(newValue) => handleToggleChange(index, newValue)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setShowMarkersSelection(false)}> {t("close")}</button>
                    </div>
                </div>
            )}

            {/* Location Permission Overlay */}
            {showLocationOverlay && (
                <div className='overlay-steps'>
                    <div className='overlay-content'>
                        <h3>{t("location_permission.title")}</h3>
                        <p>{t("location_permission.description")}</p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button onClick={() => setShowLocationOverlay(false)}>
                                {t("location_permission.close")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );

};







const GameSteps = () => {
    const { t, i18n } = useTranslation();
    const [inputValue, setInputValue] = useState('');
    const [overlayType, setOverlayType] = useState(null);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const hasTimedOut = useRef(false);

    const [step, setStep] = useState(null);
    const [gameId, setGameId] = useState(null);
    const [stepId, setStepId] = useState(null);
    const [playId, setPlayId] = useState(null);
    const [imagePath, setImagePath] = useState('');
    const [showIntroGuide, setShowIntroGuide] = useState(false);


    const navigate = useNavigate();
    const location = useLocation();


    const handleInputChange = (event) => {
        setInputValue(event.target.value);
    };

    // Add this near your other state variables in GameSteps
    const [windowDimension, setWindowDimension] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    useEffect(() => {
        const detectSize = () => {
            setWindowDimension({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        window.addEventListener('resize', detectSize);
        return () => window.removeEventListener('resize', detectSize);
    }, []);

    const handleIconClick = (overlay) => {
        if (overlay === "map") {
            enterFullScreen();
        }
        // Handle icon click logic
        // For example, you can set the overlay type and visibility here
        setOverlayType(overlay);
        setOverlayVisible(true);


    };

    const enterFullScreen = () => {
        if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen();
        } else if (document.documentElement.mozRequestFullScreen) { // Firefox
            document.documentElement.mozRequestFullScreen();
        } else if (document.documentElement.webkitRequestFullscreen) { // Chrome, Safari, Opera
            document.documentElement.webkitRequestFullscreen();
        } else if (document.documentElement.msRequestFullscreen) { // IE/Edge
            document.documentElement.msRequestFullscreen();
        }
    };



    const skipStep = async () => {
        // Close the overlay
        setOverlayVisible(false);

        console.log("Skip step " + step.step);

        const newData = {
            game: gameId,
            step: Number(stepId) + 1,
            play_id: playId,
        };

        const gameContentProvider = new GameContentProvider();
        let token;
        token = localStorage.getItem('token');
        if (!token) {
            token = localStorage.getItem('guest_token');
        }

        const result = await gameContentProvider.skipStep(stepId, token, playId);
        console.log(result);
        //Clear value
        setInputValue("");

        //Not final step. Open next step
        navigate(location.pathname, { state: newData, replace: true });
    }

    const closeOverlay = async (overlay) => {
        // Close the overlay
        setOverlayVisible(false);
        setOverlayType(null);

        if (overlay == "correct" || overlay == "final") {
            //Start next step
            const newData = {
                game: gameId,
                step: Number(stepId) + 1,
                play_id: playId,
            };

            //Report step has been completed
            const gameContentProvider = new GameContentProvider();
            let token;
            token = localStorage.getItem('token');
            if (!token) {
                console.log("kur2");
                token = localStorage.getItem('guest_token');
            }

            console.log("Current token: " + token);
            const result = await gameContentProvider.stepCompleted(stepId, token, playId);

            if (result) {
                console.log("Open next step for: ", newData);

                if (overlay == "correct") {
                    //Clear value
                    setInputValue("");

                    //Not final step. Open next step
                    navigate(location.pathname, { state: newData, replace: true });
                } else {
                    //Final step
                    navigate("/game-overview", { state: newData, replace: true });
                }


            } else {
                console.log("Error completing the step");
            }

        }
    };

    const handleNextStep = (overlay) => {
        console.log(inputValue);
        console.log(step.answer);


        if (!step.answer.some(item => item.toLowerCase().trim() === inputValue.toLowerCase().trim())) {
            //Code is incorrect
            handleIconClick('wrong');
        } else if (step.finalStep == 1) {
            // This is the last step
            //Start screen 
            handleIconClick('final');
            console.log("Final Step - Congratulations");
        } else {
            handleIconClick('correct');

        }
    };

    useEffect(() => {
        const handleMessage = (event) => {
            const data = event.data;
            if (data && data.game && data.step) {
                setGameId(data.game);
                setStepId(data.step);
                setPlayId(data.play_id);

                // Store the data in sessionStorage
                sessionStorage.setItem('game', data.game);
                sessionStorage.setItem('step', data.step);
                sessionStorage.setItem('play_id', data.play_id);

                console.log('game:', data.game);
                console.log('step:', data.step);
                console.log('play_id:', data.play_id);
            }
        };

        // Load data from sessionStorage when the page loads
        const storedGameId = sessionStorage.getItem('game');
        const storedStepId = sessionStorage.getItem('step');
        const storedPlayId = sessionStorage.getItem('play_id');

        if (storedGameId && storedStepId && storedPlayId) {
            setGameId(storedGameId);
            setStepId(storedStepId);
            setPlayId(storedPlayId);
        }

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);



    useEffect(() => {
        const { game, step, play_id, type } = location.state || {};
        if (game) {
            setGameId(game);
        }
        if (step) {
            setStepId(step);
        }

        if (play_id) {
            setPlayId(play_id);
        }

    }, [location.state]);


    useEffect(() => {

        const fetchData = async () => {
            try {
                const gameContentProvider = new GameContentProvider();
                const stepsDetails = await gameContentProvider.getStepDetails(gameId, stepId, i18n.language, playId);
                console.log(stepsDetails);
                setStep(stepsDetails[0]);
                //setImage(stepsDetails[0].image);

                if (stepId == 1 && !localStorage.getItem(`intro_shown_${playId}`)) {
                    setShowIntroGuide(true);
                }
            } catch (error) {
                console.error('Error fetching game details:', error);
                // Handle error as needed
            }

        };

        if (gameId != null && gameId != undefined && stepId != null && stepId != undefined && playId != null && playId != undefined) {
            fetchData();
        }

    }, [gameId, stepId, playId]);

    const [syncTargetStep, setSyncTargetStep] = useState(null);
    useEffect(() => {
        if (!playId || !stepId) return;

        const checkSync = async () => {
            // Don't re-trigger if we're already showing an overlay
            if (overlayType === 'final' || overlayType === 'sync') return;

            try {
                const gameContentProvider = new GameContentProvider();
                let token = localStorage.getItem('token') || localStorage.getItem('guest_token');

                const syncResult = await gameContentProvider.getCurrentStep(playId, token);
                if (!syncResult) return; // null = network error, skip silently

                const [currentServerStep, gameCompleted] = syncResult;

                if (gameCompleted) {
                    setOverlayType('final');
                    setOverlayVisible(true);
                    return;
                }

                // Compare as numbers — stepId from sessionStorage is a string
                if (Number(currentServerStep) > Number(stepId)) {
                    setSyncTargetStep(currentServerStep);
                    exitFullScreen();
                    setInputValue("");
                    setOverlayType('sync');
                    setOverlayVisible(true);
                }
            } catch (error) {
                console.error('Error syncing game state:', error);
            }
        };

        const syncInterval = setInterval(checkSync, 10000);
        return () => clearInterval(syncInterval);

    }, [gameId, stepId, playId, location.pathname, overlayType]); // Added overlayType to dependencies

    const exitFullScreen = () => {
        const isFullScreen = document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement;

        if (isFullScreen) {
            if (document.exitFullscreen) {
                document.exitFullscreen().catch(err => console.warn("Fullscreen exit error:", err));
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    };

    useEffect(() => {
        const updateImagePath = () => {
            if (!step || !step.image) {
                // Prevent updates if step or step.image is not available
                setImagePath('');
                return;
            }

            const orientation = window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
            const folderPath = `../assets/steps/${orientation}`;
            setImagePath(`${folderPath}/${step.image}`);
        };

        // Set initial image path
        updateImagePath();

        // Add an event listener to handle orientation changes
        const handleOrientationChange = () => {
            //console.log("Orienation changed");
            if (step && step.image) {
                // Only update if step and step.image are available
                updateImagePath();
            }
        };

        window.addEventListener('resize', handleOrientationChange);

        // Cleanup the event listener
        return () => window.removeEventListener('resize', handleOrientationChange);
    }, [step]);

    const [showRemaining, setShowRemaining] = useState(false);
    const [elapsedTime, setElapsedTime] = useState('');
    const [remainingTime, setRemainingTime] = useState('');

    useEffect(() => {
        if (!step || !step.start_date || !step.end_date) return;

        const interval = setInterval(() => {
            const now = new Date();
            const start = new Date(step.start_date);
            const end = new Date(step.end_date);

            const elapsedMs = Math.max(0, now - start);
            const remainingMs = Math.max(0, end - now);
            //remainingMs = 0;

            // 🔥 NEW LOGIC: Check if time has run out
            if (remainingMs <= 0 && !hasTimedOut.current) {
                hasTimedOut.current = true; // Mark as timed out so it doesn't loop
                clearInterval(interval);    // Stop the clock
                exitFullScreen();           // Close the map if it's open
                setInputValue("");          // Clear any typed answers
                setOverlayType('timeout');  // Show the timeout overlay
                setOverlayVisible(true);
            }

            const formatTime = (ms) => {
                const totalSeconds = Math.floor(ms / 1000);
                const hours = Math.floor(totalSeconds / 3600);
                const minutes = Math.floor((totalSeconds % 3600) / 60);
                const seconds = totalSeconds % 60;
                return `${hours}h ${minutes}m ${seconds}s`;
            };

            setElapsedTime(formatTime(elapsedMs));
            setRemainingTime(formatTime(remainingMs));
        }, 1000);

        return () => clearInterval(interval);
    }, [step?.start_date, step?.end_date]);

    const toggleTimeView = () => setShowRemaining((prev) => !prev);



    if (!step) {
        return <div>{t('loading')}...</div>;
    }

    if (!imagePath) {
        // Optionally render a placeholder or nothing when the image path is not available
        return null;
    }

    return (
        <div className="your-component" style={{ position: 'relative', overflow: 'hidden' }}>
            <div
                className="background"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url(${imagePath})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(2px)', // Adjust blur level
                    zIndex: -1,
                }}
            ></div>


            <div className="content-container">
                {/* Text description */}

                {/* Timer in top-left corner */}
                {step && step.start_date && step.end_date && overlayType !== 'map' && (
                    <div
                        className="timer-box"
                        onClick={toggleTimeView}
                        style={{
                            position: 'absolute',
                            top: 10,
                            left: 10,
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: '#fff',
                            padding: '10px 15px',
                            borderRadius: '10px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            zIndex: 10,
                            userSelect: 'none',
                        }}
                    >
                        {showRemaining
                            ? `⏳ ${t('remaining_time')}: ${remainingTime}`
                            : `🕒 ${t('elapsed_time')}: ${elapsedTime}`}
                    </div>
                )}

                {/* Step progress in bottom-left corner */}
                {step && step.step !== undefined && step.total_steps !== undefined && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 10,
                            left: 10,
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: '#fff',
                            padding: '8px 12px',
                            borderRadius: '10px',
                            fontSize: '20px',
                            zIndex: 10,
                        }}
                    >
                        {step.step} / {step.total_steps}
                    </div>
                )}

                {/* Input field and button centered at the bottom */}
                <div className="input-container">
                    <h1>{step.text}</h1>


                    <input
                        type="text"
                        placeholder={t('enter_something')}
                        value={inputValue}
                        onChange={handleInputChange}
                    />
                    <button onClick={() => handleNextStep()}>{t('submit')}</button>
                </div>



                {/* Icons on the right side */}
                <div className="icon-container">
                    {/* Icon 1 */}
                    <div className="icon" onClick={() => handleIconClick('hints')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f6d365', color: 'white', fontSize: '24px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)', cursor: 'pointer' }}>
                        <FaLightbulb />
                    </div>

                    {/* Icon 2 */}
                    <div className="icon" onClick={() => handleIconClick('map')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#4facfe', color: 'white', fontSize: '24px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)', cursor: 'pointer' }}>
                        <FaMapMarkedAlt />
                    </div>

                    {step.skip_step === 1 && (
                        <div className="icon" onClick={() => handleIconClick('skip')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#a18cd1', color: 'white', fontSize: '24px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)', cursor: 'pointer' }}>
                            <FaFastForward />
                        </div>
                    )}


                </div>

                {/* Introduction Guide */}
                {showIntroGuide && (
                    <IntroGuideOverlay
                        hasSkip={step.skip_step === 1}
                        onClose={() => {
                            localStorage.setItem(`intro_shown_${playId}`, 'true');
                            setShowIntroGuide(false);
                        }}
                    />
                )}

                {/* Overlay view */}
                {overlayVisible && (
                    <div className={` ${overlayType === 'map' ? 'map-overlay-steps' : 'overlay-steps'}`}>
                        {/* 🔥 ADD THE CONFETTI HERE 🔥 */}
                        {overlayType === 'final' && (
                            <Confetti
                                width={windowDimension.width}
                                height={windowDimension.height}
                                numberOfPieces={400} /* Makes it a dense, exciting burst! */
                                gravity={0.15} /* Makes it fall nicely */
                                style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999 }} /* Ensures it covers everything */
                            />
                        )}

                        <div className={` ${overlayType === 'map' ? 'overlay-content-map' : 'overlay-content'}`}>
                            {overlayType === 'hints' && (
                                <div>
                                    <h2>{t('hints')}</h2>
                                    <p2>{step.hint1}</p2>
                                    <br />
                                    <button onClick={() => closeOverlay("hints")}>{t('close')}</button>
                                </div>
                            )}
                            {overlayType === 'skip' && (
                                <div>
                                    <h2>{t('confirm_skip')}</h2>
                                    <button onClick={() => skipStep()}>{t('skip')}</button>
                                    <button onClick={() => closeOverlay("hints")}>{t('close')}</button>
                                </div>
                            )}

                            {overlayType === 'map' &&
                                <MapOverlay
                                    closeOverlay={closeOverlay}
                                    recommendations={step.recommendations}
                                    city={step.city}
                                />
                            }

                            {overlayType === 'sync' && (
                                <div>
                                    <h2>{t('game_synced') || "Game Synced!"}</h2>
                                    <p style={{ marginBottom: '20px' }}>
                                        {t('teammate_advanced_step') || "A teammate completed this step! Moving to the next one..."}
                                    </p>
                                    <button onClick={() => {
                                        // 1. Close overlay
                                        setOverlayVisible(false);
                                        setOverlayType(null);

                                        // 2. Navigate to the new step
                                        const newData = {
                                            game: gameId,
                                            step: syncTargetStep,
                                            play_id: playId,
                                        };
                                        navigate(location.pathname, { state: newData, replace: true });
                                    }}>
                                        {t('go_to_next_step') || "Continue"}
                                    </button>
                                </div>
                            )}


                            {overlayType === 'wrong' && (
                                <div>
                                    <h2>{t('code_is_incorrect')}</h2>
                                    <button onClick={() => closeOverlay("wrong")}>{t('close')}</button>
                                </div>
                            )}

                            {overlayType === 'correct' && (
                                <div>
                                    <h2>{t('correct')} !</h2>
                                    {step.guide && (
                                        <p className="step-guide-text">{step.guide}</p>
                                    )}
                                    <button onClick={() => closeOverlay("correct")}>{t('go_to_next_step')}</button>
                                </div>
                            )}

                            {/* TIME UP OVERLAY */}
                            {overlayType === 'timeout' && (
                                <div>
                                    <h2>⏰ {t('time_is_up') || "Time is up!"}</h2>
                                    <p style={{ marginBottom: '20px' }}>
                                        {t('game_over_time') || "You have run out of time for this game."}
                                    </p>
                                    <button onClick={() => {
                                        setOverlayVisible(false);
                                        setOverlayType(null);

                                        // Navigate to the game overview page
                                        const newData = {
                                            game: gameId,
                                            step: stepId,
                                            play_id: playId,
                                        };
                                        navigate("/game-overview", { state: newData, replace: true });
                                    }}>
                                        {t('go_to_overview') || "View Results"}
                                    </button>
                                </div>
                            )}


                            {overlayType === 'final' && (
                                <div>
                                    <h2>🎉 {t('correct')} ! 🎉</h2>
                                    <p style={{ marginBottom: '20px' }}>{t('congratulations_on_completing_the_game')}</p>
                                    {step.guide && (
                                        <p className="step-guide-text">{step.guide}</p>
                                    )}
                                    <button onClick={() => closeOverlay("final")}>{t('close')}</button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

            </div>
        </div>
    );

};

export default GameSteps;




